const IMAGE_MIME_PREFIX = "image/"
const VIDEO_MIME_PREFIX = "video/"
const AUDIO_MIME_PREFIX = "audio/"
const MEDIA_PLACEHOLDER_RE = /^\s*MEDIA\s*$/i
const MEDIA_DIRECTIVE_LINE_RE = /^\s*MEDIA\s*:\s*(\S+)\s*$/i

const MIME_BY_EXTENSION = new Map([
  ["png", "image/png"],
  ["jpg", "image/jpeg"],
  ["jpeg", "image/jpeg"],
  ["gif", "image/gif"],
  ["webp", "image/webp"],
  ["svg", "image/svg+xml"],
  ["mp4", "video/mp4"],
  ["webm", "video/webm"],
  ["mov", "video/quicktime"],
  ["mp3", "audio/mpeg"],
  ["wav", "audio/wav"],
  ["m4a", "audio/mp4"],
  ["ogg", "audio/ogg"],
])

export function inferArtifactKind({ kind, mime, mime_type: mimeType } = {}) {
  const explicitKind = typeof kind === "string" ? kind.trim().toLowerCase() : ""
  if (["image", "gif", "video", "audio"].includes(explicitKind)) {
    return explicitKind
  }

  const mediaMime = typeof mime === "string" && mime ? mime : mimeType
  const normalizedMime = typeof mediaMime === "string" ? mediaMime.trim().toLowerCase() : ""
  if (normalizedMime === "image/gif") return "image"
  if (normalizedMime.startsWith(IMAGE_MIME_PREFIX)) return "image"
  if (normalizedMime.startsWith(VIDEO_MIME_PREFIX)) return "video"
  if (normalizedMime.startsWith(AUDIO_MIME_PREFIX)) return "audio"
  return undefined
}

export function normalizeTaskArtifact(artifact) {
  if (!artifact || typeof artifact !== "object") return null

  const signedUrl =
    typeof artifact.signed_url === "string" && artifact.signed_url.trim()
      ? artifact.signed_url.trim()
      : typeof artifact.url === "string" && artifact.url.trim()
        ? artifact.url.trim()
        : ""

  const mime =
    typeof artifact.mime === "string" && artifact.mime.trim()
      ? artifact.mime.trim()
      : typeof artifact.mime_type === "string" && artifact.mime_type.trim()
        ? artifact.mime_type.trim()
        : undefined
  const filename =
    typeof artifact.filename === "string" && artifact.filename.trim()
      ? artifact.filename.trim()
      : undefined
  const storagePath =
    typeof artifact.storage_path === "string" && artifact.storage_path.trim()
      ? artifact.storage_path.trim()
      : typeof artifact.openclaw_path === "string" && artifact.openclaw_path.trim()
        ? artifact.openclaw_path.trim()
        : typeof artifact.media_id === "string" && artifact.media_id.trim()
          ? artifact.media_id.trim()
          : signedUrl
  if (!signedUrl && !storagePath) return null
  const sizeBytes = Number.isFinite(artifact.size_bytes)
    ? artifact.size_bytes
    : undefined
  const kind = inferArtifactKind({ ...artifact, mime })

  return {
    type: "media",
    storage_path: storagePath,
    ...(signedUrl ? { signed_url: signedUrl } : {}),
    ...(kind ? { kind } : {}),
    ...(mime ? { mime } : {}),
    ...(filename ? { filename } : {}),
    ...(sizeBytes !== undefined ? { size_bytes: sizeBytes } : {}),
  }
}

export function normalizeTaskArtifacts(artifacts) {
  if (!Array.isArray(artifacts)) return []
  const normalized = []
  const seen = new Set()
  for (const artifact of artifacts) {
    const next = normalizeTaskArtifact(artifact)
    if (!next) continue
    const key = `${next.storage_path}|${next.signed_url}`
    if (seen.has(key)) continue
    seen.add(key)
    normalized.push(next)
  }
  return normalized
}

function inferMimeFromUrl(url) {
  if (typeof url !== "string" || !url.trim()) return undefined
  const trimmed = url.trim()
  const withoutQuery = trimmed.split(/[?#]/, 1)[0] || trimmed
  const lastSegment = withoutQuery.split("/").pop() || ""
  const ext = lastSegment.includes(".") ? lastSegment.split(".").pop()?.toLowerCase() : ""
  return ext ? MIME_BY_EXTENSION.get(ext) : undefined
}

function inferFilenameFromUrl(url) {
  if (typeof url !== "string" || !url.trim()) return undefined
  const withoutQuery = url.trim().split(/[?#]/, 1)[0]
  const lastSegment = withoutQuery.split("/").pop() || ""
  return lastSegment || undefined
}

export function extractMediaArtifactsFromResultText(result) {
  if (typeof result !== "string" || !result.trim()) return []

  const artifacts = []
  for (const line of result.split(/\r?\n/)) {
    const match = line.match(MEDIA_DIRECTIVE_LINE_RE)
    if (!match) continue
    const signedUrl = match[1]?.trim()
    if (!signedUrl) continue
    artifacts.push({
      signed_url: signedUrl,
      storage_path: signedUrl,
      mime: inferMimeFromUrl(signedUrl),
      filename: inferFilenameFromUrl(signedUrl),
    })
  }
  return normalizeTaskArtifacts(artifacts)
}

function stripMediaDirectiveLines(result) {
  if (typeof result !== "string" || !result) return ""
  const lines = result.split(/\r?\n/)
  const kept = []
  for (const line of lines) {
    if (MEDIA_DIRECTIVE_LINE_RE.test(line)) {
      continue
    }
    kept.push(line)
  }
  return kept.join("\n").replace(/\n{3,}/g, "\n\n").trim()
}

export function normalizeTaskResultText(result, artifacts) {
  const text = typeof result === "string" ? result : ""
  const renderableArtifacts = normalizeTaskArtifacts(artifacts)
  if (MEDIA_PLACEHOLDER_RE.test(text) && renderableArtifacts.length > 0) {
    return ""
  }
  if (renderableArtifacts.length > 0) {
    return stripMediaDirectiveLines(text)
  }
  return text
}
